import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Sparkles } from 'lucide-react';

interface User {
  id: string;
  display: string;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  users: User[];
  placeholder?: string;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export function MentionInput({ value, onChange, users, placeholder, className, onKeyDown }: MentionInputProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [filter, setFilter] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onChange(val);

    const cursor = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursor);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    if (lastAtSymbol !== -1 && !textBeforeCursor.slice(lastAtSymbol).includes(' ')) {
      const filterText = textBeforeCursor.slice(lastAtSymbol + 1);
      setFilter(filterText);
      setShowPicker(true);
      
      // Calculate position
      const textarea = e.target;
      const { offsetLeft, offsetTop } = textarea;
      // Basic positional estimation
      setPickerPosition({ top: offsetTop + 20, left: offsetLeft + (filterText.length * 8) });
    } else {
      setShowPicker(false);
    }
  };

  const selectUser = (user: User) => {
    const cursor = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = value.slice(0, cursor);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    const newValue = 
      value.slice(0, lastAtSymbol) + 
      `@${user.display} ` + 
      value.slice(cursor);
      
    onChange(newValue);
    setShowPicker(false);
    textareaRef.current?.focus();
  };

  return (
    <div className="relative w-full">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleInput}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
      />
      {showPicker && (
        <div 
          ref={pickerRef}
          className="absolute z-50 bg-white border border-black/10 rounded-xl shadow-xl w-64 max-h-48 overflow-y-auto"
          style={{ top: 'auto', bottom: '100%', marginBottom: '8px' }}
        >
          {users
            .filter(u => u.display.toLowerCase().includes(filter.toLowerCase()))
            .map(u => (
              <button
                key={u.id}
                type="button"
                className="w-full text-left p-3 text-xs hover:bg-black/5 font-bold uppercase tracking-wider flex items-center justify-between group"
                onClick={() => selectUser(u)}
              >
                <span className="flex items-center gap-2">
                  {u.id === 'ai-bot-assistant' && <Sparkles className="w-3 h-3 text-primary" />}
                  {u.display}
                </span>
                {u.id === 'ai-bot-assistant' && (
                  <span className="text-[8px] bg-primary/10 text-primary px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    READY
                  </span>
                )}
              </button>
            ))
          }
        </div>
      )}
    </div>
  );
}
